"use client";

import Link from "next/link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import Image from "next/image";

import {
  Globe2,
  ShieldCheck,
  TrendingUp,
  Layers3,
  Handshake,
} from "lucide-react";

const stats = [
  { value: "40+", label: "Countries on the network" },
  { value: "12K+", label: "Verified buyers & suppliers" },
  { value: "₹850Cr+", label: "Trade value facilitated" },
  { value: "3x", label: "Faster quote-to-order" },
];

const pillars = [
  {
    icon: ShieldCheck,
    title: "Trusted Trade Network",
    desc: "Verified buyers and suppliers with secure and transparent transactions.",
  },
  {
    icon: Layers3,
    title: "Execution in One System",
    desc: "Manage sourcing, RFQs, quotes, and orders in one unified workflow.",
  },
  {
    icon: TrendingUp,
    title: "Decision Intelligence",
    desc: "AI insights to help you take smarter business decisions.",
  },
];

export default function AboutPage() {
  return (
    <div className="bg-white dark:bg-slate-950">
      {/* HERO */}
      <section className="py-20 border-b bg-gradient-to-b from-slate-50 to-white text-center">
        <div className="container mx-auto px-4">
          <Badge className="bg-orange-100 text-orange-700">
            About DoExim
          </Badge>

          <h1 className="mt-4 text-4xl sm:text-5xl font-bold max-w-3xl mx-auto">
            Operating layer for{" "}
            <span className="text-orange-600">modern global sourcing</span>
          </h1>

          <p className="mt-4 text-muted-foreground max-w-2xl mx-auto">
            DoExim helps B2B trade teams manage buyers, suppliers, RFQs,
            quotes, and orders in one intelligent platform.
          </p>
        </div>
      </section>

      {/* STATS */}
      <section className="grid grid-cols-2 sm:grid-cols-4 text-center border-b">
        {stats.map((item) => (
          <div key={item.label} className="py-8 border-r last:border-r-0">
            <h3 className="text-2xl font-bold text-orange-600">{item.value}</h3>
            <p className="text-sm text-muted-foreground mt-1">{item.label}</p>
          </div>
        ))}
      </section>

      {/* WHY */}
      <section className="py-16">
        <div className="container mx-auto px-4 grid md:grid-cols-2 gap-10">
          <div>
            <h2 className="text-2xl font-semibold mb-4">Why DoExim Exists</h2>

            <p className="text-muted-foreground leading-7">
              Most export-import businesses still rely on spreadsheets, emails,
              and disconnected tools. DoExim centralizes the entire workflow
              for faster, cleaner, and more efficient trade execution.
            </p>

            <ul className="mt-6 space-y-3 text-sm">
              {[
                "Faster quote-to-decision cycles",
                "Better supplier visibility",
                "Reduced sourcing friction",
                "Improved global trade planning",
              ].map((item) => (
                <li key={item} className="flex gap-2">
                  <span className="w-2 h-2 bg-orange-500 rounded-full mt-2" />
                  {item}
                </li>
              ))}
            </ul>
          </div>

          <div className="space-y-4">
            {pillars.map((p) => (
              <div key={p.title}>
                <div className="w-8 h-[2px] bg-orange-500 mb-2" />
                <h3 className="font-semibold">{p.title}</h3>
                <p className="text-sm text-muted-foreground">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 border-b">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-5xl mx-auto">
            <p className="text-xs font-semibold tracking-widest text-orange-600 uppercase mb-2">
              Global reach
            </p>

            <h2 className="text-2xl sm:text-3xl font-semibold mb-2">
              India at the center of every corridor
            </h2>

            <p className="text-muted-foreground mb-6 text-sm sm:text-base">
              DoExim connects Indian exporters and importers to verified trade
              partners across 40+ countries.
            </p>

            {/* MAP */}
            <div className="relative w-full h-[420px]">
              <Image
                src="/worldmap.png"
                alt="World Map"
                fill
                priority
                className="object-contain rounded-xl opacity-90"
              />
             
              {/* 🌍 Other countries dots */}
              <div className="absolute top-[40%] left-[70%] w-2 h-2 bg-orange-400 rounded-full"></div>{" "}
              {/* USA */}
              <div className="absolute top-[35%] left-[30%] w-2 h-2 bg-orange-400 rounded-full"></div>{" "}
              {/* Europe */}
              <div className="absolute top-[50%] left-[65%] w-2 h-2 bg-orange-400 rounded-full"></div>{" "}
              {/* Middle East */}
              <div className="absolute top-[60%] left-[75%] w-2 h-2 bg-orange-400 rounded-full"></div>{" "}
              {/* Australia */}
              <div className="absolute top-[55%] left-[40%] w-2 h-2 bg-orange-400 rounded-full"></div>{" "}
              {/* Africa */}
              
             
            </div>
          </div>
        </div>
      </section>

      {/* FLOW */}
      <section className="py-16 border-t border-b">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl font-semibold mb-6">How it works</h2>

          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-6">
            {["Post RFQ", "Get quotes", "Place order", "Track & measure"].map(
              (step, i) => (
                <div key={step}>
                  <div className="text-3xl font-bold text-orange-200">
                    0{i + 1}
                  </div>
                  <h4 className="font-semibold">{step}</h4>
                  <p className="text-sm text-muted-foreground">
                    Efficient workflow with complete transparency.
                  </p>
                </div>
              ),
            )}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 text-center">
        <h2 className="text-xl font-semibold">
          Ready to scale your trade business?
        </h2>

        <p className="text-muted-foreground mt-2">
          Join thousands of buyers and suppliers worldwide.
        </p>

        <div className="mt-6 flex justify-center gap-3">
          <Button asChild>
            <Link href="/welcome/contact">
              <Handshake className="mr-2 h-4 w-4" />
              Contact Sales
            </Link>
          </Button>

          <Button variant="outline" asChild>
            <Link href="/welcome/pricing">View Plans</Link>
          </Button>
        </div>
      </section>
    </div>
  );
}
